import { getStoredToken } from './auth';

// Check if a token is expired (handles both Google JWT and Discord base64 tokens)
function isTokenExpired(token: string): boolean {
  try {
    // Check if it's a JWT (has 3 parts separated by dots)
    if (token.includes('.')) {
      // Google JWT token
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(jsonPayload);
      const exp = payload.exp * 1000; // Convert to milliseconds
      return Date.now() >= exp;
    } else {
      // Discord base64 token
      const decoded = JSON.parse(atob(token));
      if (decoded.timestamp) {
        const tokenAge = Date.now() - decoded.timestamp;
        return tokenAge > 24 * 60 * 60 * 1000; // 24 hours
      }
      return false;
    }
  } catch (e) {
    console.error('Failed to check token expiration:', e);
    return true; // Assume expired if we can't parse it
  }
}

/**
 * Make an authenticated API request with JWT token
 * Use this for any backend endpoints that need user verification
 */
export function authenticatedFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  return new Promise((resolve, reject) => {
    let token: string | null = null;
    
    try {
      token = getStoredToken();
      
      // Check if token is expired
      if (token && isTokenExpired(token)) {
        console.warn('Token expired, clearing stored credentials');
        localStorage.removeItem('autocorrect_adventure_token');
        localStorage.removeItem('autocorrect_adventure_user');
        return reject(new Error('Your session has expired. Please sign in again.'));
      }
    } catch (e) {
      if (e instanceof Error && e.message.includes('expired')) {
        return reject(e);
      }
      console.error('Error in authenticatedFetch:', e);
    }
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    resolve(
      fetch(url, {
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      })
    );
  });
}

/**
 * Submit a win to the leaderboard
 * Backend verifies the JWT token before saving
 */
export async function submitWin(data: {
  difficulty: number;
  gameMode: string;
  turns: number;
  rewindPenalties: number;
  isDailyChallenge?: boolean;
  dailyChallengeDate?: string;
}): Promise<{ success: boolean; message?: string }> {
  try {
    console.log('Submitting win to leaderboard:', data);
    
    const response = await authenticatedFetch('/api/leaderboard?action=submit', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    console.log('Leaderboard submit response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Leaderboard submit error response:', errorText);
      
      let errorDetails = `HTTP ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        if (errorJson.error) errorDetails += `: ${errorJson.error}`;
        if (errorJson.details) errorDetails += ` - ${errorJson.details}`;
      } catch {
        if (errorText) errorDetails += `: ${errorText}`;
      }
      
      throw new Error(errorDetails);
    }

    const result = await response.json();
    console.log('Leaderboard submit success:', result);
    return result;
  } catch (error) {
    console.error('Failed to submit win:', error);
    if (error instanceof Error) {
      console.error('Error details:', error.message, error.stack);
    }
    throw error;
  }
}

/**
 * Get leaderboard data with optional filters
 */
export async function getLeaderboard(options?: {
  difficulty?: number;
  gameMode?: string;
  limit?: number;
  daily?: string;
}): Promise<{
  success: boolean;
  entries: Array<{
    userId: string;
    userName: string;
    userPicture: string;
    wins: number;
    lastWinTimestamp: number;
    username?: string;
  }>;
  total: number;
}> {
  try {
    const params = new URLSearchParams({ action: 'get' });
    if (options?.difficulty) params.append('difficulty', options.difficulty.toString());
    if (options?.gameMode) params.append('gameMode', options.gameMode);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.daily) params.append('daily', options.daily);

    const response = await fetch(`/api/leaderboard?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error);
    throw error;
  }
}

/**
 * Get the current user's username
 */
export async function getUsername(): Promise<{ success: boolean; username: string | null }> {
  try {
    const response = await authenticatedFetch('/api/user?action=username');

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to get username:', error);
    throw error;
  }
}

/**
 * Set the current user's username
 */
export async function setUsername(username: string): Promise<{ success: boolean; username: string }> {
  try {
    const response = await authenticatedFetch('/api/user?action=username', {
      method: 'POST',
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to set username:', error);
    throw error;
  }
}

/**
 * Save an adventure (autosave)
 */
export async function saveAdventure(adventure: any): Promise<{ success: boolean; adventureId: string }> {
  try {
    const response = await authenticatedFetch('/api/adventures?action=save', {
      method: 'POST',
      body: JSON.stringify(adventure),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to save adventure:', error);
    throw error;
  }
}

/**
 * List user's saved adventures
 */
export async function listAdventures(status?: 'in_progress' | 'completed'): Promise<{
  success: boolean;
  adventures: any[];
  total: number;
}> {
  try {
    const params = new URLSearchParams({ action: 'list' });
    if (status) params.append('status', status);

    const response = await authenticatedFetch(`/api/adventures?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to list adventures:', error);
    throw error;
  }
}

/**
 * Load a specific adventure
 */
export async function loadAdventure(id: string): Promise<{ success: boolean; adventure: any }> {
  try {
    const response = await authenticatedFetch(`/api/adventures?action=load&id=${encodeURIComponent(id)}`);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to load adventure:', error);
    throw error;
  }
}

/**
 * Delete a saved adventure
 */
export async function deleteAdventure(id: string): Promise<{ success: boolean }> {
  try {
    const response = await authenticatedFetch(`/api/adventures?action=delete&id=${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error('Failed to delete adventure:', error);
    throw error;
  }
}
